import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, MapPin, CheckCircle, Plane, ShieldCheck, Building, Ruler, TrendingUp, Phone, Mail, MessageCircle, Wifi } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Particuliers = () => {
  const { t, language } = useLanguage();

  const schengenCountries = [
    'Allemagne', 'Autriche', 'Belgique', 'Danemark', 'Espagne', 'Estonie', 
    'Finlande', 'France', 'Grèce', 'Hongrie', 'Islande', 'Italie', 
    'Lettonie', 'Liechtenstein', 'Lituanie', 'Luxembourg', 'Malte', 
    'Norvège', 'Pays-Bas', 'Pologne', 'Portugal', 'République tchèque', 
    'Slovaquie', 'Slovénie', 'Suède', 'Suisse'
  ];

  return (
    <div data-testid="particuliers-page">
      {/* Hero Header */}
      <section className="page-header page-header-particuliers" data-testid="particuliers-header">
        <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans animate-fade-in">{t('home.space')} {t('home.particuliers')}</p>
        <h1 className="font-serif text-4xl md:text-6xl text-white mb-6 animate-fade-in-delay-1">
          {t('particuliers.title')}
        </h1>
        <div className="gold-line gold-line-center mb-6 animate-fade-in-delay-2"></div>
        <p className="text-white/80 text-lg max-w-2xl text-center animate-fade-in-delay-3">
          {t('particuliers.subtitle')}
        </p>
      </section>

      {/* Services Overview Grid */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="services-overview">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('particuliers.services')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
              {t('particuliers.complete')}
            </h2>
            <div className="gold-line gold-line-center"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Visa Schengen */}
            <div className="service-card text-center group" data-testid="service-visa-schengen">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Globe className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('particuliers.visaSchengen')}</h3>
              <p className="text-gray-600 text-sm">
                {t('particuliers.visaSchengenDesc')}
              </p>
            </div>

            {/* Visa Canada */}
            <div className="service-card text-center group" data-testid="service-visa-canada">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Plane className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('particuliers.visaCanada')}</h3>
              <p className="text-gray-600 text-sm">
                {t('particuliers.visaCanadaDesc')}
              </p>
            </div>

            {/* Gestion Immobilière */}
            <div className="service-card text-center group" data-testid="service-gestion-immo">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Building className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('particuliers.gestionImmo')}</h3>
              <p className="text-gray-600 text-sm">
                {t('particuliers.gestionImmoDesc')}
              </p>
            </div>

            {/* Maîtrise d'Œuvre */}
            <div className="service-card text-center group" data-testid="service-maitrise-oeuvre">
              <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C9A227] transition-all duration-500">
                <Ruler className="w-8 h-8 text-[#C9A227] group-hover:text-white transition-colors duration-500" />
              </div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-4">{t('particuliers.maitriseOeuvre')}</h3>
              <p className="text-gray-600 text-sm">
                {t('particuliers.maitriseOeuvreDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gestion Immobilière et Maîtrise d'Œuvre - Detailed */}
      <section className="section-luxury bg-white" data-testid="immobilier-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center">
                  <Building className="w-8 h-8 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#C9A227] font-sans">{t('particuliers.service')}</p>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{t('particuliers.gestionImmoTitle')}</h2>
                </div>
              </div>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('particuliers.gestionImmoText')}
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.gestionLocative')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.rechercheLocataires')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.suiviLoyers')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.entretien')}</span>
                </li>
              </ul>
            </div>
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center">
                  <Ruler className="w-8 h-8 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#C9A227] font-sans">{t('particuliers.service')}</p>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{t('particuliers.maitriseOeuvreTitle')}</h2>
                </div>
              </div>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('particuliers.maitriseOeuvreText')}
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.conception')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.coordination')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.suiviChantier')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.reception')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Visa Schengen */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="visa-schengen-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center">
                  <Globe className="w-8 h-8 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#C9A227] font-sans">{t('particuliers.service')}</p>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{t('particuliers.visaSchengen')}</h2>
                </div>
              </div>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('particuliers.schengenText')}
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.analyseConseil')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.constitution')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.verification')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.preparation')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">{t('particuliers.suivi')}</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-8 border border-[#F5EFD3]">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-6 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C9A227]" />
                {t('particuliers.schengenCountries')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {schengenCountries.map((country) => (
                  <span 
                    key={country} 
                    className="px-3 py-1 bg-[#FDFCF8] text-gray-600 text-sm border border-[#F5EFD3]"
                  >
                    {country}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visa Canada */}
      <section className="section-luxury bg-white" data-testid="visa-canada-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1715638477502-b99aab059f83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxjYW5hZGElMjB0b3JvbnRvJTIwc2t5bGluZSUyMGx1eHVyeXxlbnwwfHx8fDE3NzE1MDg0Nzh8MA&ixlib=rb-4.1.0&q=85" 
                alt="Canada" 
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center">
                  <Plane className="w-8 h-8 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#C9A227] font-sans">{t('particuliers.service')}</p>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{t('particuliers.visaCanada')}</h2>
                </div>
              </div>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {t('particuliers.canadaText')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-2">{t('particuliers.visaTouriste')}</h4>
                  <p className="text-gray-600 text-sm">{t('particuliers.visitCanada')}</p>
                </div>
                <div className="p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-2">{t('particuliers.visaEtudiant')}</h4>
                  <p className="text-gray-600 text-sm">{t('particuliers.etudes')}</p>
                </div>
                <div className="p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-2">{t('particuliers.workvisa')}</h4>
                  <p className="text-gray-600 text-sm">{t('particuliers.workOpportunities')}</p>
                </div>
                <div className="p-4 bg-[#FDFCF8] border border-[#F5EFD3]">
                  <h4 className="font-serif text-lg text-[#1A1A1A] mb-2">{t('particuliers.residence')}</h4>
                  <p className="text-gray-600 text-sm">{t('particuliers.immigration')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visa Success Rate */}
      <section className="section-luxury bg-[#1A1A1A]" data-testid="visa-success-rate-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('particuliers.successRate')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              {t('particuliers.ourSuccessRate')}
            </h2>
            <div className="gold-line gold-line-center mb-8"></div>
            <p className="text-white/70 text-lg leading-relaxed">
              {t('particuliers.successRateText')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="text-center p-8 border border-[#C9A227]/30 bg-[#C9A227]/5" data-testid="success-rate-card">
              <TrendingUp className="w-10 h-10 text-[#C9A227] mx-auto mb-4" />
              <p className="font-serif text-5xl text-[#C9A227] mb-2">{t('particuliers.successRateValue')}</p>
              <p className="text-white/80 text-sm">{t('particuliers.successRateLabel')}</p>
            </div>
            <div className="text-center p-8 border border-white/10 bg-white/5" data-testid="competitor-rate-card">
              <p className="text-sm uppercase tracking-widest text-white/50 mb-4">{t('particuliers.vsCompetitors')}</p>
              <p className="font-serif text-5xl text-white/40 mb-2">{t('particuliers.competitorRate')}</p>
              <p className="text-white/50 text-sm">{t('particuliers.competitorRateLabel')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Visa Contact Information */}
      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="visa-contact-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('particuliers.visaContactTitle')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
              {t('particuliers.visaContactSubtitle')}
            </h2>
            <div className="gold-line gold-line-center"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <a href="https://wa.me/33184919293" target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-6 bg-white border border-[#F5EFD3] group hover:border-[#C9A227] transition-all duration-300" data-testid="visa-contact-phone">
              <div className="w-14 h-14 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-300">
                <MessageCircle className="w-6 h-6 text-[#C9A227] group-hover:text-white transition-colors" />
              </div>
              <div>
                <p className="font-serif text-lg text-[#1A1A1A] mb-1">{t('particuliers.visaPhone')}</p>
                <p className="text-[#C9A227] font-medium">+33 1 84 91 92 93</p>
                <p className="text-gray-500 text-sm mt-1 italic">{t('particuliers.whatsappOnly')}</p>
              </div>
            </a>
            <a href="mailto:visas@excelsium.online" className="flex items-start gap-4 p-6 bg-white border border-[#F5EFD3] group hover:border-[#C9A227] transition-all duration-300" data-testid="visa-contact-email">
              <div className="w-14 h-14 rounded-full border border-[#C9A227] flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A227] transition-all duration-300">
                <Mail className="w-6 h-6 text-[#C9A227] group-hover:text-white transition-colors" />
              </div>
              <div>
                <p className="font-serif text-lg text-[#1A1A1A] mb-1">{t('particuliers.visaEmail')}</p>
                <p className="text-[#C9A227] font-medium">visas@excelsium.online</p>
                <p className="text-gray-500 text-sm mt-1 italic">{t('particuliers.visaEmailDesc')}</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Domotique */}
      <section className="section-luxury bg-white" data-testid="domotique-section">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full border border-[#C9A227] flex items-center justify-center">
                  <Wifi className="w-8 h-8 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-widest text-[#C9A227] font-sans">{t('particuliers.service')}</p>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{t('particuliers.domotique')}</h2>
                </div>
              </div>
              <div className="gold-line mb-8"></div>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">{t('particuliers.domotiqueDesc')}</p>
              <div className="grid grid-cols-1 gap-3">
                {['domotiqueDetail1','domotiqueDetail2','domotiqueDetail3','domotiqueDetail4','domotiqueDetail5','domotiqueDetail6'].map((key) => (
                  <div key={key} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600 text-sm">{t(`particuliers.${key}`)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="w-full max-w-md p-8 bg-[#FDFCF8] border border-[#F5EFD3]">
                <Wifi className="w-12 h-12 text-[#C9A227] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-center text-[#1A1A1A] mb-3">{t('particuliers.domotique')}</h3>
                <div className="gold-line gold-line-center mb-4"></div>
                <p className="text-gray-500 text-center text-sm">{t('particuliers.domotiqueDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-luxury bg-[#1A1A1A]" data-testid="process-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans">{t('particuliers.process')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              {t('particuliers.howWeWork')}
            </h2>
            <div className="gold-line gold-line-center"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="process-step-1">
              <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-white">1</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-4">{t('particuliers.consultation')}</h3>
              <p className="text-white/60 text-sm">{t('particuliers.consultationDesc')}</p>
            </div>

            <div className="text-center" data-testid="process-step-2">
              <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-white">2</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-4">{t('particuliers.preparationStep')}</h3>
              <p className="text-white/60 text-sm">{t('particuliers.preparationStepDesc')}</p>
            </div>

            <div className="text-center" data-testid="process-step-3">
              <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-white">3</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-4">{t('particuliers.submission')}</h3>
              <p className="text-white/60 text-sm">{t('particuliers.submissionDesc')}</p>
            </div>

            <div className="text-center" data-testid="process-step-4">
              <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center mx-auto mb-6">
                <span className="font-serif text-2xl text-white">4</span>
              </div>
              <h3 className="font-serif text-xl text-white mb-4">{t('particuliers.supportStep')}</h3>
              <p className="text-white/60 text-sm">{t('particuliers.supportStepDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-luxury bg-[#FDFCF8]" data-testid="particuliers-cta-section">
        <div className="container-luxury">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 rounded-full border-2 border-[#C9A227] flex items-center justify-center mx-auto mb-8">
              <ShieldCheck className="w-10 h-10 text-[#C9A227]" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1A1A1A] mb-6">
              {t('particuliers.readyToStart')}
            </h2>
            <div className="gold-line gold-line-center mb-8"></div>
            <p className="text-gray-600 text-lg mb-10">
              {t('particuliers.contactForConsultation')}
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="particuliers-final-cta">
              {t('particuliers.requestConsultation')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Particuliers;
